import getClientExternal from './_get-client-external';
import globals from '../../utils/globals';
import useLoadable from '../use-loadable';
import useLoadableBulk from '../use-loadable-bulk';
import { BASE_PATH_BY_API_TYPE, PROFILE_API_TYPES } from './_constants';
import { useEffect } from 'react';

const resolvePath = ({ apiType, path }) => {
  let basePath = BASE_PATH_BY_API_TYPE[apiType];

  if (apiType !== PROFILE_API_TYPES.platform) {
    if (globals.env === 'acceptance2') {
      basePath = basePath.replace(/\/v(?=\d+)/, '-a2/v');
    }

    if (globals.env === 'acceptance3') {
      basePath = basePath.replace(/\/v(?=\d+)/, '-a3/v');
    }

    if (globals.env === 'acceptance4') {
      basePath = basePath.replace(/\/v(?=\d+)/, '-a4/v');
    }
  }

  if (!basePath) {
    throw new Error(`API Error: Invalid API type: ${apiType}`);
  }

  return `${basePath}${path}`;
};

const callApiGet = ({ client, apiType, path, params }) => {
  const pathResolved = resolvePath({ path, apiType });
  return params
    ? client.get(pathResolved, { params })
    : client.get(pathResolved);
};

const callApiPost = ({ client, apiType, path, params }) => {
  const pathResolved = resolvePath({ path, apiType });

  return params ? client.post(pathResolved, params) : client.post(pathResolved);
};

const callApiExternalGet = ({ url, params }) => {
  const client = getClientExternal();
  return params ? client.get(url, { params }) : client.get(url);
};

const useApiBase = ({
  client,
  apiType,
  path,
  handler,
  verb,
  params,
  shouldLoad = true,
  dependencies = []
}) => {
  const { load, ...state } = useLoadable({
    callback: () => handler({ client: client.data, apiType, path, params }),
    actionPrefix: `API [${verb}] ${path}`
  });

  useEffect(() => {
    if (client.isLoaded && !client.isLoading && shouldLoad) {
      load();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    client.isLoaded,
    client.isLoading,
    apiType,
    path,
    shouldLoad,
    verb,
    ...dependencies // eslint-disable-line react-hooks/exhaustive-deps
  ]);

  return state;
};

const useApiBulkBase = ({
  apiType,
  client,
  configs,
  debugLabel,
  handler,
  verb,
  shouldLoad = true,
  dependencies = []
}) => {
  const { load, ...state } = useLoadableBulk({
    callbacks: configs.map(config => () =>
      handler({ client: client.data, apiType, ...config })
    ),
    actionPrefix: `Bulk API [${verb}] ${debugLabel}`
  });

  const configsString = JSON.stringify(configs);
  useEffect(() => {
    if (client.isLoaded && !client.isLoading && shouldLoad) {
      load();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    client.isLoaded,
    client.isLoading,
    configsString,
    apiType,
    shouldLoad,
    verb,
    ...dependencies // eslint-disable-line react-hooks/exhaustive-deps
  ]);

  return state;
};

const useApiExternalBulkBase = ({
  configs,
  debugLabel,
  handler,
  verb,
  shouldLoad = true,
  dependencies = []
}) => {
  const { load, ...state } = useLoadableBulk({
    callbacks: configs.map(config => () =>
      handler({ url: config.url, params: config.params })
    ),
    actionPrefix: `Bulk API [${verb}] ${debugLabel}`
  });

  const configsString = JSON.stringify(configs);
  useEffect(() => {
    if (shouldLoad) {
      load();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    configsString,
    shouldLoad,
    verb,
    ...dependencies // eslint-disable-line react-hooks/exhaustive-deps
  ]);

  return state;
};

const useApiExternalBase = ({
  handler,
  url,
  params,
  verb,
  shouldLoad = true,
  dependencies = []
}) => {
  const { load, ...state } = useLoadable({
    callback: () => handler({ url, params }),
    actionPrefix: `API [${verb}] ${url}`
  });

  useEffect(() => {
    if (shouldLoad) {
      load();
    }
  }, [shouldLoad, url, verb, ...dependencies]); // eslint-disable-line react-hooks/exhaustive-deps

  return state;
};

export const useApiGet = ({ client, ...config }) =>
  useApiBase({
    ...config,
    client,
    handler: callApiGet,
    verb: 'get'
  });

export const useApiGetBulk = ({ apiType, client, configs, debugLabel, shouldLoad }) =>
  useApiBulkBase({
    apiType,
    configs,
    client,
    shouldLoad,
    debugLabel,
    handler: callApiGet,
    verb: 'get'
  });

export const useApiExternalGetBulk = ({ configs, debugLabel, shouldLoad }) =>
  useApiExternalBulkBase({
    configs,
    shouldLoad,
    debugLabel,
    handler: callApiExternalGet,
    verb: 'get'
  });

export const useApiPost = ({ client, ...config }) =>
  useApiBase({
    ...config,
    client,
    handler: callApiPost,
    verb: 'post'
  });

export const useApiExternalGet = ({ config }) =>
  useApiExternalBase({
    ...config,
    handler: callApiExternalGet,
    verb: 'get'
  });

export const screenerApiPost = ({
  client,
  apiType,
  path,
  params
}) => {
  const response = callApiPost({ client: client.service, apiType, path, params });
  return response;
};

export const screenerApiGet = ({
  client,
  apiType,
  path
}) => {
  return callApiGet({ client: client.service, apiType, path });
};

export const screenerExternalApiGet = url => {
  return callApiExternalGet({ url });
};
